"use client";

import * as React from "react";
import { Box, Card, Flex, Text } from "@radix-ui/themes";
import * as CardList from "./card-list.js";
import { Button, Skeleton } from "./elements.js";
import {
  ExternalLinkIcon,
  Cross2Icon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { ProviderIcon } from "./provider-icon.js";
import { IconPanel } from "./icon-panel.js";
import { GenericHttpsIcon } from "./audit-log-stream-icons.js";
import { Status } from "./status.js";
import {
  getDomProps,
  type WidgetRootState,
  type WidgetRootDomProps,
} from "./utils.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";
import { getErrorMessage } from "./generic-error.js";
import type { AuditLogStreamType } from "../api/endpoint.js";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface NotConfiguredProps {
  connectionStatus: "NotConfigured";
}

interface InactiveProps {
  connectionStatus: "Inactive";
  destinationType: AuditLogStreamType;
}

interface ActiveProps {
  connectionStatus: "Active";
  destinationType: AuditLogStreamType;
  lastSyncedEventId?: string | null;
}

interface ErrorProps {
  connectionStatus: "Error";
  destinationType: AuditLogStreamType;
  lastSyncedEventId?: string | null;
}

const AdminPortalAuditLogStreamingContext = React.createContext<{
  connectionStatus: AdminPortalAuditLogStreamingStatusProps["connectionStatus"];
} | null>(null);

function useAdminPortalAuditLogStreamingContext() {
  const context = React.useContext(AdminPortalAuditLogStreamingContext);
  if (!context) {
    throw new Error(
      "useAdminPortalAuditLogStreamingContext must be used within a AdminPortalAuditLogStreamingContext provider",
    );
  }
  return context;
}

export type AdminPortalAuditLogStreamingStatusProps =
  | NotConfiguredProps
  | InactiveProps
  | ActiveProps
  | ErrorProps;

type AdminPortalAuditLogStreamingProps = WidgetRootDomProps &
  AdminPortalAuditLogStreamingStatusProps & {
    adminPortalOpenButton: React.ReactNode;
  };

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Maps AuditLogStreamType to display name
 */
function getDestinationName(type: AuditLogStreamType): string {
  switch (type) {
    case "Datadog":
      return "Datadog";
    case "Splunk":
      return "Splunk";
    case "S3":
      return "AWS S3";
    case "GoogleCloudStorage":
      return "Google Cloud Storage";
    case "AzureSentinel":
      return "Azure Sentinel";
    case "GenericHttps":
      return "Generic HTTPS";
    default:
      return type;
  }
}

/**
 * Maps AuditLogStreamType to ProviderIcon provider name
 */
function getDestinationIcon(
  type: AuditLogStreamType,
): "datadog" | "splunk" | "aws" | "google-cloud" | "azure" | undefined {
  switch (type) {
    case "Datadog":
      return "datadog";
    case "Splunk":
      return "splunk";
    case "S3":
      return "aws";
    case "GoogleCloudStorage":
      return "google-cloud";
    case "AzureSentinel":
      return "azure";
    default:
      return undefined;
  }
}

/**
 * Helper to get the correct WidgetRootDomProps based on state
 */
function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "admin-portal-audit-log-streaming",
    widgetState: state,
  });
}

const CROCKFORD_BASE32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function decodeUlidTimestamp(ulid: string): Date | null {
  if (!ulid || ulid.length < 10) return null;
  let timestamp = 0;
  for (let i = 0; i < 10; i++) {
    const charIndex = CROCKFORD_BASE32.indexOf(ulid.charAt(i).toUpperCase());
    if (charIndex === -1) return null;
    timestamp = timestamp * 32 + charIndex;
  }
  return new Date(timestamp);
}

function getRelativeTimeString(currentDate: Date, pastDate: Date): string {
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const diff = pastDate.getTime() - currentDate.getTime();
  const diffSeconds = Math.round(diff / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);
  const diffMonths = Math.round(diffDays / 30);
  const diffYears = Math.round(diffMonths / 12);

  if (Math.abs(diffSeconds) < 60) return "now";
  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, "minute");
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  if (Math.abs(diffDays) < 30) return rtf.format(diffDays, "day");
  if (Math.abs(diffMonths) < 12) return rtf.format(diffMonths, "month");
  return rtf.format(diffYears, "year");
}

function AdminPortalAuditLogStreamingButton({
  isPending,
  href,
  initConfig,
}: {
  isPending: boolean;
  href: string | null;
  initConfig: () => void;
}) {
  const { connectionStatus } = useAdminPortalAuditLogStreamingContext();
  const label = (() => {
    switch (connectionStatus) {
      case "NotConfigured":
        return (
          <Translation
            defaultMessage="Set up Log Streams"
            id="s3Wu7U"
            description="Button label to start log stream setup"
          />
        );
      case "Inactive":
        return (
          <Translation
            defaultMessage="Continue setup"
            id="/oM/HY"
            description="Button label to continue incomplete log stream setup"
          />
        );
      case "Active":
      case "Error":
        return (
          <Translation
            defaultMessage="Manage"
            id="+k5uUi"
            description="Button label to manage log stream settings"
          />
        );
    }
  })();

  if (href) {
    return (
      <Button variant="secondary" asChild>
        <a href={href} target="_blank" rel="noopener noreferrer">
          {label} <ExternalLinkIcon aria-hidden />
        </a>
      </Button>
    );
  }

  return (
    <Button
      variant="secondary"
      loading={isPending}
      disabled={isPending}
      onClick={initConfig}
    >
      {label} <ExternalLinkIcon aria-hidden />
    </Button>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminPortalAuditLogStreaming: React.FC<
  AdminPortalAuditLogStreamingProps
> = (props) => {
  const { connectionStatus, adminPortalOpenButton, className } = props;

  const destinationType =
    connectionStatus !== "NotConfigured"
      ? (props as InactiveProps | ActiveProps | ErrorProps).destinationType
      : null;
  const lastSyncedEventId =
    connectionStatus === "Active" || connectionStatus === "Error"
      ? (props as ActiveProps | ErrorProps).lastSyncedEventId
      : undefined;
  const lastSyncedAt = lastSyncedEventId
    ? decodeUlidTimestamp(lastSyncedEventId)
    : null;

  const icon = destinationType ? getDestinationIcon(destinationType) : null;

  return (
    <AdminPortalAuditLogStreamingContext.Provider value={{ connectionStatus }}>
      <CardList.Root {...getWidgetRootDomProps("resolved", { className })}>
        <CardList.Item>
          <Flex direction="row" justify="between" align="center" gap="2">
            <Flex gap="4" align="center">
              {connectionStatus === "NotConfigured" ? (
                <Text size="2" color="gray">
                  <Translation
                    defaultMessage="You haven't set up Log Streams yet."
                    id="n4mruE"
                    description="Empty state message when log streaming is not configured"
                  />
                </Text>
              ) : (
                <>
                  <IconPanel color="panel">
                    {icon ? (
                      <ProviderIcon provider={icon} size="2" />
                    ) : (
                      <GenericHttpsIcon />
                    )}
                  </IconPanel>
                  {lastSyncedAt ? (
                    <Flex direction="column">
                      <Text size="2" weight="bold">
                        {getDestinationName(destinationType!)}
                      </Text>
                      <Text size="2" color="gray">
                        <Translation
                          defaultMessage="Last sync {relativeTime}"
                          id="fT9f0p"
                          description="Label showing when the last log stream sync occurred"
                          values={{
                            relativeTime: getRelativeTimeString(
                              new Date(),
                              lastSyncedAt,
                            ),
                          }}
                        />
                      </Text>
                    </Flex>
                  ) : (
                    <Text size="2" weight="bold">
                      {getDestinationName(destinationType!)}
                    </Text>
                  )}
                </>
              )}
            </Flex>

            <Flex gap="5" align="center">
              {connectionStatus === "NotConfigured" ? (
                adminPortalOpenButton
              ) : (
                <>
                  {connectionStatus === "Inactive" && (
                    <Status state="waiting">
                      <Translation
                        defaultMessage="Setup in progress"
                        id="M6iKLG"
                        description="Status when log stream setup is incomplete"
                      />
                    </Status>
                  )}
                  {connectionStatus === "Active" && (
                    <Status state="success">
                      <Translation
                        defaultMessage="Streaming"
                        id="25L0J+"
                        description="Status when log stream is active"
                      />
                    </Status>
                  )}
                  {connectionStatus === "Error" && (
                    <Status state="error">
                      <Translation
                        defaultMessage="Not streaming"
                        id="7fAKZo"
                        description="Status when log stream has an error"
                      />
                    </Status>
                  )}
                  {adminPortalOpenButton}
                </>
              )}
            </Flex>
          </Flex>
        </CardList.Item>

        {connectionStatus === "Error" && (
          <CardList.Item>
            <Flex align="start" gap="2">
              <Box asChild mt="2px" flexShrink="0">
                <InfoCircledIcon color="gray" />
              </Box>
              <Text size="2" color="gray">
                <Translation
                  defaultMessage="The credentials provided are incorrect or missing. Please review and update your log stream configuration."
                  id="23tmgn"
                  description="Error message shown when audit log stream has a configuration or delivery error"
                />
              </Text>
            </Flex>
          </CardList.Item>
        )}
      </CardList.Root>
    </AdminPortalAuditLogStreamingContext.Provider>
  );
};

// ============================================================================
// LOADING COMPONENT
// ============================================================================

interface AdminPortalAuditLogStreamingLoadingProps extends WidgetRootDomProps {}

const AdminPortalAuditLogStreamingLoading: React.FC<
  AdminPortalAuditLogStreamingLoadingProps
> = (props) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("loading", props)}>
      <Flex direction="row" justify="between" align="center" gap="2">
        <Flex gap="4" align="center">
          <Skeleton>
            <IconPanel color="panel">
              <ProviderIcon provider="datadog" size="2" />
            </IconPanel>
          </Skeleton>
          <Flex direction="column" gap="1" my="-4px">
            <Skeleton>
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              <Text size="1">Datadog</Text>
            </Skeleton>
            <Skeleton>
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              <Text size="1">Last sync 10 minutes ago</Text>
            </Skeleton>
          </Flex>
        </Flex>

        <Flex gap="5" align="center">
          <Skeleton>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <Status state="success">Streaming</Status>
          </Skeleton>
          <Skeleton>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <Button variant="secondary">
              Manage <ExternalLinkIcon aria-hidden />
            </Button>
          </Skeleton>
        </Flex>
      </Flex>
    </Card>
  );
};

// ============================================================================
// ERROR COMPONENT
// ============================================================================

interface AdminPortalAuditLogStreamingErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const AdminPortalAuditLogStreamingError: React.FC<
  AdminPortalAuditLogStreamingErrorProps
> = ({ error, ...domProps }) => {
  React.useEffect(() => {
    console.error(error);
  }, [error]);

  const translate = useTranslation();
  const { heading, message } = getErrorMessage(error, translate);

  return (
    <Card size="2" {...getWidgetRootDomProps("error", domProps)}>
      <Flex direction="row" justify="between" align="center" gap="2">
        <Flex gap="4" align="center">
          {/* Error icon circle */}
          <Flex
            align="center"
            justify="center"
            width="24px"
            height="24px"
            style={{
              borderRadius: "9999px",
              backgroundColor: "var(--red-a4)",
              color: "var(--red-a11)",
              flexShrink: 0,
            }}
          >
            <Cross2Icon width="18px" height="18px" />
          </Flex>
          {/* Error text */}
          <Flex direction="column">
            <Text size="2" weight="bold">
              {heading}
            </Text>
            <Text size="2" color="gray">
              {message}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export type {
  AdminPortalAuditLogStreamingProps,
  AdminPortalAuditLogStreamingLoadingProps,
  AdminPortalAuditLogStreamingErrorProps,
};

export {
  AdminPortalAuditLogStreaming,
  AdminPortalAuditLogStreamingLoading,
  AdminPortalAuditLogStreamingError,
  /** @internal */
  AdminPortalAuditLogStreamingButton,
};
